import {
  EDITABLE_IME_STATUS,
  EE_STATUS,
  IME_STATUS,
  STATUS,
} from '~/core/constants';
import i18n from '~/core/i18n/i18n';
import type { EstimateStatus } from '~/core/models/save-estimate-dto';
import { GetEstimateDto } from '~/core/models/save-estimate-dto';
import {
  NotifierType,
  PaymentAvailable,
  SimpleMessage,
  StatusError,
  showNotification,
} from '~/legacy/components';

/**
 * Generates notifications based on a status or status change
 * @param currentStatus
 * @param estimate
 */
export const notifyFromStatus = ({
  currentStatus,
  estimate,
}: {
  currentStatus: EstimateStatus;
  estimate: GetEstimateDto;
}): EE_STATUS[] => {
  //TODO KC This has gotten out of hand - it started out so simple!

  if (!currentStatus.status || !estimate?.estimateNumber) {
    return [EE_STATUS.OPEN];
  }

  // closed
  if (currentStatus.status === STATUS.CLOSED) {
    showNotification(
      NotifierType.ERROR,
      i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.CLOSED.TITLE'),
      <StatusError
        description={i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.CLOSED.DESC')}
        message={i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.CLOSED.TITLE')}
      />,
      '',
      0
    );

    // nothing else matters, it's closed
    return [EE_STATUS.CLOSED, EE_STATUS.NO_SAVE];
  }

  // has the status changed since loaded?
  const statusUpdated = currentStatus.status !== estimate.status;

  // at this point, we know we're in an OPEN state
  const returnStatus = [EE_STATUS.OPEN];

  if (currentStatus.paymentUrl && isReadyForPayment(currentStatus, estimate)) {
    // TODO after EZE-474 is merged, ensure that we're on the last revision before allowing payment
    showNotification(
      NotifierType.SUCCESS,
      i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.READY_TO_PAY.TITLE'),
      <PaymentAvailable
        paymentUrl={currentStatus.paymentUrl}
        estimate={estimate}
      />,
      '',
      60
    );

    return [...returnStatus, EE_STATUS.CAN_PAY];
  }

  if (statusUpdated) {
    const changeMessage = (from: string | null, to: string | null) => {
      if (from && to) {
        return i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED.FROM_TO', {
          from: from,
          to: to,
        });
      }
      if (to) {
        return i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED.TO', {
          from: from,
          to: to,
        });
      }
      if (from) {
        return i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED.FROM', {
          from: from,
          to: to,
        });
      }

      return '';
    };

    showNotification(
      NotifierType.SUCCESS,
      i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED.TITLE'),
      <SimpleMessage
        description={i18n.t(
          'NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED.TITLE'
        )}
        message={changeMessage(estimate.status, currentStatus.status)}
      />,
      '',
      0
    );
  }

  if (!isAppointmentEditable(currentStatus)) {
    showNotification(
      NotifierType.WARNING,
      i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.APPOINTMENT_PENDING.TITLE'),
      <StatusError
        description={i18n.t(
          'NOTIFICATIONS.FETCH_ESTIMATE.APPOINTMENT_PENDING.DESC',
          { ...currentStatus }
        )}
        message={i18n.t(
          'NOTIFICATIONS.FETCH_ESTIMATE.APPOINTMENT_PENDING.MSG',
          { ...currentStatus }
        )}
      />,
      '',
      0
    );

    returnStatus.push(EE_STATUS.NO_SAVE);
  }

  // new error message
  if (currentStatus.imeMessage) {
    showNotification(
      NotifierType.ERROR,
      i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.EXTERNAL_ERROR.TITLE'),
      <StatusError
        description={i18n.t(
          'NOTIFICATIONS.FETCH_ESTIMATE.EXTERNAL_ERROR.DESC',
          { ...currentStatus }
        )}
        message={i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.EXTERNAL_ERROR.MSG', {
          ...currentStatus,
        })}
      />,
      '',
      0
    );

    returnStatus.push(EE_STATUS.ERROR);
  }

  return returnStatus;
};

export const getPaymentUrl = (
  status?: EstimateStatus,
  estimate?: GetEstimateDto
): string | null => status?.paymentUrl ?? estimate?.paymentUrl ?? null;

export const isLatestRevision = (estimate: GetEstimateDto): boolean =>
  // is the given estimate the most recent revision of the chain?
  !!estimate.revisions?.length &&
  estimate.revisions[estimate.revisions.length - 1]?.estimateNumber ===
    estimate?.estimateNumber;

export const isReadyForPayment = (
  status?: EstimateStatus,
  estimate?: GetEstimateDto
): boolean => {
  // must have an estimate number and a payment url, either from the fetched estimate or status update
  if (estimate?.estimateNumber && (estimate.paymentUrl || status?.paymentUrl)) {
    // use the status update if available, otherwise the estimate status rules
    const currentStatus = status?.status ?? estimate?.status;

    return (
      // and current status must be OPEN
      currentStatus === STATUS.OPEN && !!getPaymentUrl(status, estimate)
    );
  }

  return false;
};

/**
 * only certain IME STATUS values can successfully edit/save, all others get an alert
 * and probably restricted save options
 * @param status
 */
export const isAppointmentEditable = (status?: EstimateStatus): boolean =>
  !status?.imeStatus ||
  EDITABLE_IME_STATUS.includes(status?.imeStatus as IME_STATUS);

export const statusChanged = (
  prev: Partial<EstimateStatus>,
  curr: Partial<EstimateStatus>
) => {
  const prevKeys = Object.keys(prev) as Array<keyof EstimateStatus>;
  const currentKeys = Object.keys(curr) as Array<keyof EstimateStatus>;

  if (
    !prevKeys.every((key) => currentKeys.includes(key)) ||
    !currentKeys.every((key) => prevKeys.includes(key))
  ) {
    return true;
  }

  for (const key of prevKeys) {
    if (prev[key] !== curr[key]) {
      return true;
    }
  }

  return false;
};
